// src/lib/api/financial/expenseManagement.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, sanitizeInput } from '../shared/validation'
import { logger } from '../../logger'
import { buildExpenseDescription } from '@prosperia/shared/utils'

/**
 * List expenses with optional filtering
 * Migrated from supabaseApi.js lines 880-904
 * @param {string} [providedCompanyId] - Optional company ID (avoids internal lookup)
 * @param {object} [options] - Filter options
 */
const _listExpenses = async (providedCompanyId = null, options = {}) => {
  // Handle case where first arg is options object (backward compatibility)
  if (providedCompanyId && typeof providedCompanyId === 'object') {
    options = providedCompanyId
    providedCompanyId = null
  }
  
  console.log('[expenseManagement] listExpenses called')
  
  const companyId = providedCompanyId || await getValidatedCompanyId()
  if (!companyId) {
    console.warn('[expenseManagement] No company ID found')
    return createSuccessResponse([])
  }

  let query = supabase
    .from('expenses')
    .select('id, expense_date, vendor, total_amount, status, created_at')
    .eq('company_id', companyId)
    .order('expense_date', { ascending: false })

  if (Array.isArray(options.status) && options.status.length > 0) {
    query = query.in('status', options.status)
  }

  const { data, error } = await query
  if (error) {
    console.error('[expenseManagement] Error listing expenses:', error)
    throw error
  }

  console.log('[expenseManagement] listExpenses success, found', data?.length || 0, 'expenses')
  return createSuccessResponse(data || [])
}

/**
 * Create a new expense
 * Migrated from supabaseApi.js lines 905-937
 */
const _createExpense = async (expense) => {
  console.log('[expenseManagement] createExpense called with data:', expense)
  
  // Validation
  validateRequired(expense.vendor, 'Vendor')
  validateRequired(expense.expense_date, 'Expense date')
  validateRequired(expense.total_amount, 'Total amount')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const userId = await getValidatedUserId()
  if (!userId) {
    throw new Error('Not authenticated')
  }

  // Sanitize inputs
  const payload = {
    company_id: companyId,
    created_by: userId,
    // Legacy NOT NULL columns with no default (#587)
    description: buildExpenseDescription(expense),
    amount: expense.total_amount,
    vendor: sanitizeInput(expense.vendor),
    expense_date: expense.expense_date,
    total_amount: expense.total_amount,
    tax_amount: expense.tax_amount ?? 0,
    currency: expense.currency || 'USD',
    category_id: expense.category_id,
    subcategory_id: expense.subcategory_id || null,
    payment_method: expense.payment_method,
    notes: expense.notes ? sanitizeInput(expense.notes) : null,
    job_id: expense.job_id || null,
    is_billable: !!expense.is_billable,
    is_reimbursable: !!expense.is_reimbursable,
    client_id: expense.client_id || null,
    billable_description: expense.billable_description ? sanitizeInput(expense.billable_description) : null,
    status: expense.status || 'posted'
  }

  const { data, error } = await supabase
    .from('expenses')
    .insert([payload])
    .select('id')
    .single()
    
  if (error) {
    console.error('[expenseManagement] Error creating expense:', error)
    throw error
  }

  console.log('[expenseManagement] createExpense success, created expense:', data.id)
  return data
}

/**
 * Update an existing expense
 * Migrated from supabaseApi.js lines 1198-1213
 */
const _updateExpense = async (expenseId, fields) => {
  console.log('[expenseManagement] updateExpense called for ID:', expenseId, 'with fields:', fields)
  
  validateRequired(expenseId, 'Expense ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  // Sanitize string fields
  const sanitizedFields = { ...fields }
  if (sanitizedFields.vendor) {
    sanitizedFields.vendor = sanitizeInput(sanitizedFields.vendor)
  }
  if (sanitizedFields.notes) {
    sanitizedFields.notes = sanitizeInput(sanitizedFields.notes)
  }

  const { data, error } = await supabase
    .from('expenses')
    .update({ ...sanitizedFields, updated_at: new Date().toISOString() })
    .eq('id', expenseId)
    .eq('company_id', companyId)
    .select('id')
    .single()
    
  if (error) {
    console.error('[expenseManagement] Error updating expense:', error)
    throw error
  }

  console.log('[expenseManagement] updateExpense success for expense:', data.id)
  return data
}

/**
 * List expense categories
 * Migrated from supabaseApi.js lines 938-954
 */
const _listExpenseCategories = async () => {
  console.log('[expenseManagement] listExpenseCategories called')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    console.warn('[expenseManagement] No company ID found')
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('expense_categories')
    .select('id, name, parent_id, type')
    .eq('company_id', companyId)
    .order('name', { ascending: true })
    
  if (error) {
    console.error('[expenseManagement] Error listing categories:', error)
    throw error
  }

  console.log('[expenseManagement] listExpenseCategories success, found', data?.length || 0, 'categories')
  return createSuccessResponse(data || [])
}

/**
 * Create a new expense category
 * Migrated from supabaseApi.js lines 955-966
 */
const _createExpenseCategory = async ({ name, type, parent_id = null }) => {
  console.log('[expenseManagement] createExpenseCategory called with:', { name, type, parent_id })
  
  // Validation
  validateRequired(name, 'Category name')
  validateRequired(type, 'Category type')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  const { data, error } = await supabase
    .from('expense_categories')
    .insert([{ 
      company_id: companyId, 
      name: sanitizeInput(name), 
      type, 
      parent_id 
    }])
    .select('id, name, parent_id, type')
    .single()
    
  if (error) {
    console.error('[expenseManagement] Error creating category:', error)
    throw error
  }

  console.log('[expenseManagement] createExpenseCategory success, created category:', data.id)
  return data
}

/**
 * Update an expense category
 * Migrated from supabaseApi.js lines 967-980
 */
const _updateExpenseCategory = async (id, { name, parent_id, type }) => {
  console.log('[expenseManagement] updateExpenseCategory called for ID:', id, 'with data:', { name, parent_id, type })
  
  validateRequired(id, 'Category ID')
  validateRequired(name, 'Category name')
  validateRequired(type, 'Category type')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  const { data, error } = await supabase
    .from('expense_categories')
    .update({ 
      name: sanitizeInput(name), 
      parent_id, 
      type, 
      updated_at: new Date().toISOString() 
    })
    .eq('id', id)
    .eq('company_id', companyId)
    .select('id, name, parent_id, type')
    .single()
    
  if (error) {
    console.error('[expenseManagement] Error updating category:', error)
    throw error
  }

  console.log('[expenseManagement] updateExpenseCategory success for category:', data.id)
  return data
}

/**
 * Delete an expense category
 * Migrated from supabaseApi.js lines 981-995
 */
const _deleteExpenseCategory = async (id) => {
  console.log('[expenseManagement] deleteExpenseCategory called for ID:', id)
  
  validateRequired(id, 'Category ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  const { error } = await supabase
    .from('expense_categories')
    .delete()
    .eq('id', id)
    .eq('company_id', companyId)
    
  if (error) {
    console.error('[expenseManagement] Error deleting category:', error)
    throw error
  }

  console.log('[expenseManagement] deleteExpenseCategory success for category:', id)
  return { success: true }
}

/**
 * List recurring expenses
 * Migrated from supabaseApi.js lines 1100-1116
 */
const _listRecurringExpenses = async () => {
  console.log('[expenseManagement] listRecurringExpenses called')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    console.warn('[expenseManagement] No company ID found')
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('recurring_expenses')
    .select('id, vendor, amount, currency, category_id, subcategory_id, job_id, payment_method, frequency, next_run_at, active, notes')
    .eq('company_id', companyId)
    .order('next_run_at', { ascending: true })
    
  if (error) {
    console.error('[expenseManagement] Error listing recurring expenses:', error)
    throw error
  }

  console.log('[expenseManagement] listRecurringExpenses success, found', data?.length || 0, 'recurring expenses')
  return createSuccessResponse(data || [])
}

/**
 * Create a new recurring expense
 * Migrated from supabaseApi.js lines 1117-1146
 */
const _createRecurringExpense = async (payload) => {
  console.log('[expenseManagement] createRecurringExpense called with:', payload)
  
  // Validation
  validateRequired(payload.vendor, 'Vendor')
  validateRequired(payload.amount, 'Amount')
  validateRequired(payload.frequency, 'Frequency')
  validateRequired(payload.start_date || payload.next_run_at, 'Start date')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const userId = await getValidatedUserId()
  if (!userId) {
    throw new Error('Not authenticated')
  }

  const startDate = payload.start_date || payload.next_run_at;
  const insert = {
    company_id: companyId,
    created_by: userId,
    vendor: sanitizeInput(payload.vendor),
    amount: payload.amount,
    currency: payload.currency || 'USD',
    category_id: payload.category_id,
    subcategory_id: payload.subcategory_id || null,
    job_id: payload.job_id || null,
    payment_method: payload.payment_method,
    frequency: payload.frequency,
    next_run_at: payload.next_run_at || new Date(startDate).toISOString(),
    active: payload.active ?? true,
    notes: payload.notes ? sanitizeInput(payload.notes) : null
  }

  const { data, error } = await supabase
    .from('recurring_expenses')
    .insert([insert])
    .select('id')
    .single()
    
  if (error) {
    console.error('[expenseManagement] Error creating recurring expense:', error)
    throw error
  }

  console.log('[expenseManagement] createRecurringExpense success, created recurring expense:', data.id)
  return data
}

/**
 * Update a recurring expense
 * Migrated from supabaseApi.js lines 1147-1160
 */
const _updateRecurringExpense = async (id, updates) => {
  console.log('[expenseManagement] updateRecurringExpense called for ID:', id, 'with updates:', updates)
  
  validateRequired(id, 'Recurring expense ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  // Sanitize string fields
  const sanitizedUpdates = { ...updates }
  if (sanitizedUpdates.vendor) {
    sanitizedUpdates.vendor = sanitizeInput(sanitizedUpdates.vendor)
  }
  if (sanitizedUpdates.notes) {
    sanitizedUpdates.notes = sanitizeInput(sanitizedUpdates.notes)
  }

  const { data, error } = await supabase
    .from('recurring_expenses')
    .update({ ...sanitizedUpdates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('company_id', companyId)
    .select('id')
    .single()
    
  if (error) {
    console.error('[expenseManagement] Error updating recurring expense:', error)
    throw error
  }

  console.log('[expenseManagement] updateRecurringExpense success for recurring expense:', data.id)
  return data
}

/**
 * Delete a recurring expense
 * Migrated from supabaseApi.js lines 1161-1172
 */
const _deleteRecurringExpense = async (id) => {
  console.log('[expenseManagement] deleteRecurringExpense called for ID:', id)
  
  validateRequired(id, 'Recurring expense ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  const { error } = await supabase
    .from('recurring_expenses')
    .delete()
    .eq('id', id)
    .eq('company_id', companyId)
    
  if (error) {
    console.error('[expenseManagement] Error deleting recurring expense:', error)
    throw error
  }

  console.log('[expenseManagement] deleteRecurringExpense success for recurring expense:', id)
  return { success: true }
}

/**
 * Run a recurring expense immediately
 * Migrated from supabaseApi.js lines 1173-1182
 */
const _runRecurringExpenseNow = async (id) => {
  console.log('[expenseManagement] runRecurringExpenseNow called for ID:', id)
  
  validateRequired(id, 'Recurring expense ID')
  
  // Invoke the edge function to process a single schedule immediately
  const { data, error } = await supabase.functions.invoke('recurring-expense-runner', {
    body: { recurring_id: id }
  })
  
  if (error) {
    console.error('[expenseManagement] Error running recurring expense:', error)
    throw error
  }

  console.log('[expenseManagement] runRecurringExpenseNow success for recurring expense:', id)
  return data
}

/**
 * Create a draft expense for receipt attachment
 * Migrated from supabaseApi.js lines 1183-1197
 */
const _createExpenseDraft = async () => {
  console.log('[expenseManagement] createExpenseDraft called')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const userId = await getValidatedUserId()
  if (!userId) {
    throw new Error('Not authenticated')
  }

  const { data, error } = await supabase
    .from('expenses')
    .insert([{
      company_id: companyId,
      created_by: userId,
      description: 'Draft',
      amount: 0,
      vendor: 'Draft', 
      expense_date: new Date().toISOString().slice(0,10), 
      total_amount: 0, 
      category_id: null, 
      payment_method: 'OTHER', 
      status: 'draft', 
      receipt_status: 'uploaded' 
    }])
    .select('id')
    .single()
    
  if (error) {
    console.error('[expenseManagement] Error creating expense draft:', error)
    throw error
  }

  console.log('[expenseManagement] createExpenseDraft success, created draft:', data.id)
  return data
}

/**
 * Attach a receipt to an expense
 * Migrated from supabaseApi.js lines 1214-1242
 */
const _attachReceiptToExpense = async ({ expenseId, file }) => {
  console.log('[expenseManagement] attachReceiptToExpense called for expense:', expenseId, 'with file:', file?.name)
  
  validateRequired(expenseId, 'Expense ID')
  validateRequired(file, 'File')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const userId = await getValidatedUserId()
  if (!userId) {
    throw new Error('Not authenticated')
  }

  // *** CHECK STORAGE RESTRICTION ***
  const fileSizeGB = file.size / (1024 * 1024 * 1024)
  
  const { data: checkData, error: checkError } = await supabase.rpc('check_restriction', {
    p_company_id: companyId,
    p_restriction_name: 'storage_gb',
    p_increment_amount: fileSizeGB,
    p_action_name: 'upload_receipt'
  })

  if (checkError) {
    throw new Error(`Failed to check storage restrictions: ${checkError.message}`)
  }

  const restrictionCheck = checkData[0]
  if (!restrictionCheck.allowed) {
    throw new Error(`Storage limit exceeded: ${restrictionCheck.message}`)
  }

  const randomId = () => Math.random().toString(36).slice(2)
  const ext = file.name.includes('.') ? file.name.split('.').pop() : 'jpg'
  const key = `receipts/${companyId}/${userId}/${expenseId}/${randomId()}.${ext}`

  const { error: uploadError } = await supabase.storage
    .from('receipts')
    .upload(key, file, { upsert: false })
    
  if (uploadError) {
    console.error('[expenseManagement] Error uploading receipt:', uploadError)
    throw uploadError
  }

  const { data: receipt, error: insertError } = await supabase
    .from('expense_receipts')
    .insert([{ expense_id: expenseId, storage_path: key }])
    .select('id, storage_path, parsed_vendor, parsed_date, parsed_total')
    .single()
    
  if (insertError) {
    console.error('[expenseManagement] Error inserting receipt record:', insertError)
    throw insertError
  }

  await supabase
    .from('expenses')
    .update({ receipt_status: 'uploaded', updated_at: new Date().toISOString() })
    .eq('id', expenseId)

  // *** INCREMENT STORAGE USAGE AFTER SUCCESS ***
  try {
    const { error: incrementError } = await supabase.rpc('increment_usage', {
      p_company_id: companyId,
      p_restriction_name: 'storage_gb',
      p_amount: fileSizeGB
    })

    if (incrementError) {
      console.error('Failed to increment storage usage:', incrementError)
      // Don't fail the request, just log it
    }
  } catch (error) {
    console.error('Error incrementing storage usage:', error)
  }

  console.log('[expenseManagement] attachReceiptToExpense success, attached receipt:', receipt.id)
  return receipt
}

/**
 * Get receipts for an expense
 * Migrated from supabaseApi.js lines 1243-1262
 */
const _getExpenseReceipts = async (expenseId) => {
  logger.expenseManagement('getExpenseReceipts called for expense:', expenseId)
  
  validateRequired(expenseId, 'Expense ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  const { data, error } = await supabase
    .from('expense_receipts')
    .select('id, storage_path, parsed_vendor, parsed_date, parsed_total')
    .eq('expense_id', expenseId)
    .order('created_at', { ascending: false })
    
  if (error) {
    logger.error('[expenseManagement] Error getting expense receipts:', error)
    throw error
  }

  // Generate signed URLs for all receipts
  const signed = await Promise.all((data || []).map(async r => {
    try {
      const { data: signedUrl } = await supabase.storage
        .from('receipts')
        .createSignedUrl(r.storage_path, 60 * 60)
      return { ...r, url: signedUrl?.signedUrl || null }
    } catch (error) {
      logger.warn('[expenseManagement] Error generating signed URL for receipt:', r.id, error)
      return { ...r, url: null }
    }
  }))

  logger.expenseManagement('getExpenseReceipts success, found', signed.length, 'receipts')
  return signed
}


// Export all functions with error handling
export const listExpenses = withErrorHandling(_listExpenses, 'list expenses', 'expenses')
export const createExpense = withErrorHandling(_createExpense, 'create expense', 'expense')
export const updateExpense = withErrorHandling(_updateExpense, 'update expense', 'expense')
export const listExpenseCategories = withErrorHandling(_listExpenseCategories, 'list expense categories', 'expense_categories')
export const createExpenseCategory = withErrorHandling(_createExpenseCategory, 'create expense category', 'expense_category')
export const updateExpenseCategory = withErrorHandling(_updateExpenseCategory, 'update expense category', 'expense_category')
export const deleteExpenseCategory = withErrorHandling(_deleteExpenseCategory, 'delete expense category', 'expense_category')
export const listRecurringExpenses = withErrorHandling(_listRecurringExpenses, 'list recurring expenses', 'recurring_expenses')
export const createRecurringExpense = withErrorHandling(_createRecurringExpense, 'create recurring expense', 'recurring_expense')
export const updateRecurringExpense = withErrorHandling(_updateRecurringExpense, 'update recurring expense', 'recurring_expense')
export const deleteRecurringExpense = withErrorHandling(_deleteRecurringExpense, 'delete recurring expense', 'recurring_expense')
export const runRecurringExpenseNow = withErrorHandling(_runRecurringExpenseNow, 'run recurring expense now', 'recurring_expense')
export const createExpenseDraft = withErrorHandling(_createExpenseDraft, 'create expense draft', 'expense')
export const attachReceiptToExpense = withErrorHandling(_attachReceiptToExpense, 'attach receipt to expense', 'expense_receipt')
export const getExpenseReceipts = withErrorHandling(_getExpenseReceipts, 'get expense receipts', 'expense_receipts')